/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, ReactNode } from 'react';
import { Control, useController } from 'react-hook-form';
import SelectBase, { CSSObjectWithLabel } from 'react-select';
import classNames from 'classnames';

import ChevronDown from 'shared/assets/images/icons/chevron-down.svg';
import InfoIcon from 'shared/assets/images/icons/info-small.svg';
import { Tooltip } from '../Tooltip/Tooltip';
import { Typography } from '../Typography/Typography';
import styles from './Select.module.css';

export interface SelectItem {
	value: string | number | boolean;
	label: string;
}

interface SelectProps {
	options: SelectItem[];
	control: Control<any>;
	name: string;
	label: string;
	className?: string;
	defaultValue?: SelectItem;
	onSelect?: (item: SelectItem) => void;
	placeholder?: string;
	required?: boolean;
	disabled?: boolean;
	tooltipMessage?: ReactNode;
	errorMessage?: string;
	handleChange?: VoidFunction;
}

export const Select: FC<SelectProps> = ({
	control,
	name,
	options,
	className,
	label,
	defaultValue,
	placeholder,
	onSelect,
	required = false,
	disabled,
	tooltipMessage,
	errorMessage = ``,
	handleChange,
}) => {
	const { Text } = Typography;
	const {
		field,
		fieldState: { error },
	} = useController({
		control,
		name,
		rules: {
			required: {
				value: required,
				message: errorMessage,
			},
		},
	});
	const { value, onChange, ...restLangField } = field;

	return (
		<div
			className={classNames(
				styles.select__container,

				className,
			)}
		>
			<div className={classNames(styles.select__label, disabled && styles[`select__label--disabled`])}>
				<Text variant='note'>{label}</Text>
				{tooltipMessage && (
					<Tooltip
						message={tooltipMessage}
						className={styles.select__tooltip}
						size='small'
						position={[`middle`, `left-side`]}
						// isClickable
					>
						<InfoIcon />
					</Tooltip>
				)}
			</div>
			<SelectBase
				options={options}
				value={options.find(x => x.value === value)}
				onChange={option => {
					onChange(option ? option.value : option);
					if (onSelect) {
						onSelect(option as SelectItem);
					}
					if (handleChange) {
						handleChange();
					}
				}}
				classNames={{
					control: state =>
						classNames(
							styles.select__control,
							state.selectProps.menuIsOpen && styles[`select__control--pressed`],
							error && styles[`select__control--error`],
							disabled && styles[`select__control--disabled`],
						),
					valueContainer: () => styles[`select__value-container`],
					placeholder: () => styles.select__placeholder,
					indicatorsContainer: () => styles[`select__indicators-container`],
					indicatorSeparator: () => styles[`select__indicator-separator`],
					menu: () => styles.select__menu,
					menuList: () => styles[`select__menu-list`],
					option: state =>
						`${styles.select__option} ${state.isSelected && styles[`select__option--selected`]}`,
				}}
				isSearchable={false}
				styles={{
					control: (base): CSSObjectWithLabel => ({
						...base,
						boxShadow: `none`,
					}),
				}}
				components={{
					DropdownIndicator: () => (
						<span className={styles.select__indicator}>
							<ChevronDown />
						</span>
					),
				}}
				placeholder={placeholder ?? ``}
				defaultValue={defaultValue}
				{...restLangField}
			/>
			{error?.message && (
				<Text variant='note' className={styles[`select__error-text`]}>
					{error.message}
				</Text>
			)}
		</div>
	);
};
